import { getRecordedSameOriginPreviousRoute } from "@/app/components/navigation/clientRouteHistory";

interface LocationBrowserBackSafetyInput {
  currentHref: string;
  historyLength: number;
  previousHref: string | null;
  referrerHref: string | null;
}

interface NavigationHistoryEntryLike {
  index?: number;
  url?: string | null;
}

interface NavigationApiLike {
  currentEntry?: NavigationHistoryEntryLike | null;
  entries?: () => readonly NavigationHistoryEntryLike[];
}

function hasSameOrigin(candidateHref: string | null, currentHref: string) {
  if (!candidateHref) return false;

  try {
    return new URL(candidateHref).origin === new URL(currentHref).origin;
  } catch {
    return false;
  }
}

export function isLocationBrowserBackTargetSafe({
  currentHref,
  historyLength,
  previousHref,
  referrerHref,
}: LocationBrowserBackSafetyInput) {
  if (historyLength <= 1) return false;

  if (previousHref !== null) {
    return hasSameOrigin(previousHref, currentHref);
  }

  return hasSameOrigin(referrerHref, currentHref);
}

function readPreviousNavigationEntryHref() {
  const navigation = (
    window as Window & { navigation?: NavigationApiLike }
  ).navigation;
  const currentIndex = navigation?.currentEntry?.index;
  const entries = navigation?.entries?.();

  if (
    typeof currentIndex !== "number" ||
    !Number.isSafeInteger(currentIndex) ||
    currentIndex <= 0 ||
    !entries
  ) {
    return null;
  }

  return (
    entries.find((entry) => entry.index === currentIndex - 1)?.url ??
    entries[currentIndex - 1]?.url ??
    null
  );
}

/**
 * Allows native Back only when the browser can prove the previous entry is on
 * the same Volera origin. A reused tab, direct embed, external referrer, or an
 * unavailable history destination falls back to Home instead of leaving the
 * widget or exposing the host page as an accidental destination.
 */
export function canLocationPageNavigateBackWithinOrigin() {
  if (typeof window === "undefined") return false;

  return isLocationBrowserBackTargetSafe({
    currentHref: window.location.href,
    historyLength: window.history.length,
    previousHref: readPreviousNavigationEntryHref(),
    referrerHref: document.referrer || null,
  });
}

/** Exact previous-page check for chooser → catalog → chooser navigation. */
export function canLocationPageNavigateBackToPath(pathname: string) {
  if (typeof window === "undefined" || window.history.length <= 1) return false;
  const previousHref = readPreviousNavigationEntryHref();
  if (!previousHref) return false;

  try {
    const previous = new URL(previousHref, window.location.href);
    return previous.origin === window.location.origin &&
      previous.pathname === pathname;
  } catch {
    return false;
  }
}

/** Skip repeated catalog entries left by returning from a later booking step. */
export function getLocationServicesBackDistance(servicesPathname: string): number | null {
  if (typeof window === "undefined" || window.history.length <= 1 ||
    window.location.pathname !== servicesPathname) return null;

  try {
    const navigation = (window as Window & { navigation?: NavigationApiLike }).navigation;
    const currentIndex = navigation?.currentEntry?.index;
    const entries = navigation?.entries?.();
    if (typeof currentIndex === "number" && Number.isSafeInteger(currentIndex) && entries) {
      for (let index = currentIndex - 1; index >= 0; index -= 1) {
        const href = entries.find((entry) => entry.index === index)?.url ?? entries[index]?.url;
        if (!href) return null;
        const previous = new URL(href, window.location.href);
        if (previous.origin !== window.location.origin) return null;
        if (previous.pathname !== servicesPathname) return currentIndex - index;
      }
      return null;
    }

    const href = getRecordedSameOriginPreviousRoute(window.location.href);
    if (!href) return null;
    const previous = new URL(href, window.location.href);
    return previous.origin === window.location.origin && previous.pathname !== servicesPathname ? 1 : null;
  } catch {
    return null;
  }
}
